// src/pages/technician/TechnicianDashboard.jsx
//
// Technician's "My Jobs" home screen.
//
// D-306 (16 Oct 2026): a technician may have several jobs in progress at
// the same time (the backend allowed it since D-224). The old
// "finish the active job first" block on this screen is removed — every
// assigned + accepted job shows its own Start Work button.
//
// D-307: every visible text comes from technicianStrings.js via t(),
// so the screen follows the app-wide English / Hinglish choice.

import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import {
  getTechTasks,
  acceptJob,
  rejectJob,
} from "../../pages/services/serviceTaskService";

import { runSync } from "../../services/offlineSyncService";
import { retryOnPermissionDenied } from "../../utils/retryOnPermissionDenied";
import { useLang } from "../../i18n/useLang";
import LangToggle from "../../i18n/LangToggle";

import {
  SERVICE_STATUS,
  STATUS_PRIORITY,
} from "../../constants/enums/serviceEnums";

// ── Status config — premium colors (labels come from technicianStrings) ────
const STATUS_CONFIG = {
  assigned:                   { labelKey: "status_assigned",         gradient: "from-blue-500 to-blue-600",     icon: "📋" },
  in_progress:                { labelKey: "status_in_progress",      gradient: "from-amber-500 to-orange-500",  icon: "⚡" },
  partial:                    { labelKey: "status_partial",          gradient: "from-orange-500 to-red-500",    icon: "⚡" },
  completed_pending_approval: { labelKey: "status_pending_approval", gradient: "from-violet-500 to-purple-600", icon: "⏳" },
};

// ── Card bg by status ─────────────────────────────────────────────────────────
const CARD_BG = {
  assigned:                   "from-white to-slate-50",
  in_progress:                "from-amber-50 to-orange-50",
  partial:                    "from-orange-50 to-red-50",
  completed_pending_approval: "from-violet-50 to-purple-50",
};

export default function TechnicianDashboard() {
  const navigate  = useNavigate();
  const { t }     = useLang();
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";
  const techId    = user.uid || user.id || "";
  const techName  = user.name || "";

  const [tasks,      setTasks]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [isOnline,   setIsOnline]   = useState(navigator.onLine);
  const [pendingOps, setPendingOps] = useState([]);

  // confirmation removed — direct navigate

  // ── Reject modal ──────────────────────────────────────────────────────────
  const [rejectModal,  setRejectModal]  = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // ── Online/Offline ────────────────────────────────────────────────────────
  useEffect(() => {
    const onOnline = async () => {
      setIsOnline(true);
      const result = await runSync();
      if (result.synced > 0) { loadTasks(); }
      updatePendingOps();
    };
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online",  onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online",  onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const updatePendingOps = async () => {
  try {
    const { getUnsyncedActions } = await import("../../services/offlineDB");
    const pending = await getUnsyncedActions();
    setPendingOps(pending);
  } catch {
    setPendingOps([]);
  }
};

  // ── Load tasks ────────────────────────────────────────────────────────────
  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      // Wrapped in retryOnPermissionDenied — covers the post-login
      // claims race (company_id custom claim not yet committed
      // server-side) for a brand-new technician's very first fetch
      // right after OTP login (12 Aug 2026 audit).
      const data = await retryOnPermissionDenied(() => getTechTasks({ company_id: companyId, tech_id: techId }));
      setTasks(data.sort((a, b) => {
        const pA = STATUS_PRIORITY[a.status] || 9;
        const pB = STATUS_PRIORITY[b.status] || 9;
        if (pA !== pB) return pA - pB;
        return (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0);
      }));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [companyId, techId]);

  useEffect(() => {
    loadTasks();
    updatePendingOps();
    if (navigator.onLine && companyId && techId) {
      runSync()
        .then(r => { if (r.synced > 0) loadTasks(); })
        .catch(console.error);
    }
  }, [loadTasks]);

  // ── Accept ────────────────────────────────────────────────────────────────
  const handleAccept = async (task_id) => {
    try {
      await acceptJob({ task_id, tech_id: techId, tech_name: techName });
      setTasks(prev => prev.map(task =>
        task.id === task_id
          ? { ...task, assigned_techs: (task.assigned_techs || []).map(tech =>
              tech.id === techId ? { ...tech, accepted: true } : tech) }
          : task
      ));
    } catch (err) { alert(err.message || t("accept_failed")); }
  };

  // ── Reject ────────────────────────────────────────────────────────────────
  const handleReject = async () => {
    if (!rejectReason.trim()) { alert(t("reject_reason_required")); return; }
    try {
      await rejectJob({ task_id: rejectModal.task_id, tech_id: techId, tech_name: techName, reason: rejectReason.trim() });
      setTasks(prev => prev.filter(task => task.id !== rejectModal.task_id));
      setRejectModal(null);
      setRejectReason("");
    } catch (err) { alert(err.message || t("reject_failed")); }
  };

  const getTechStatus = (task) =>
    (task.assigned_techs || []).find(tech => tech.id === techId) || null;

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen pb-24 bg-slate-50">

      {/* ── TOP BAR ── */}
      <div className="px-4 pt-5 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-teal-600 uppercase tracking-widest font-semibold">ZENO ERP</p>
            <h1 className="text-2xl font-bold text-slate-800 mt-0.5">{t("my_jobs")}</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {t("welcome_name", { name: techName || t("technician_fallback") })}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <LangToggle />
            <div className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
              isOnline ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${isOnline ? "bg-green-400" : "bg-red-400"}`} />
              {isOnline ? t("online") : t("offline")}
            </div>
            {pendingOps.length > 0 && (
              <span className="text-[10px] bg-amber-500/20 text-amber-400 font-semibold px-2 py-1 rounded-full">
                {t("pending_sync", { count: pendingOps.length })}
              </span>
            )}
          </div>
        </div>

        {/* Offline banner */}
        {!isOnline && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
            {t("offline_banner")}
          </div>
        )}
      </div>

      {/* ── STATS ROW ── */}
      {!loading && tasks.length > 0 && (
        <div className="px-4 mb-4 grid grid-cols-3 gap-2">
          {[
            { key: "active",   label: t("stat_active"),   count: tasks.filter(task => [SERVICE_STATUS.IN_PROGRESS, SERVICE_STATUS.PARTIAL].includes(task.status)).length, color: "text-amber-400" },
            { key: "assigned", label: t("stat_assigned"), count: tasks.filter(task => task.status === SERVICE_STATUS.ASSIGNED).length, color: "text-blue-400" },
            { key: "pending",  label: t("stat_pending"),  count: tasks.filter(task => task.status === SERVICE_STATUS.COMPLETED_PENDING_APPROVAL).length, color: "text-violet-400" },
          ].map(s => (
            <div key={s.key} className="bg-white border border-slate-200 rounded-2xl p-3 text-center shadow-sm">
              <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── TASK LIST ── */}
      <div className="px-4 space-y-3">

        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="h-40 rounded-2xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">👷</div>
            <p className="text-slate-300 font-semibold text-lg">{t("no_jobs_title")}</p>
            <p className="text-slate-500 text-sm mt-1">{t("no_jobs_body")}</p>
          </div>
        ) : (
          tasks.map(task => {
            const cfg      = STATUS_CONFIG[task.status];
            const label    = cfg ? t(cfg.labelKey) : task.status;
            const gradient = cfg?.gradient || "from-slate-600 to-slate-700";
            const icon     = cfg?.icon || "📋";
            const cardBg   = CARD_BG[task.status] || "from-slate-800 to-slate-900";
            const techInfo = getTechStatus(task);
            const accepted = techInfo?.accepted;
            const isActive = task.status === SERVICE_STATUS.IN_PROGRESS || task.status === SERVICE_STATUS.PARTIAL;

            return (
              <div key={task.id}
                className={`bg-gradient-to-br ${cardBg} rounded-2xl overflow-hidden border border-slate-200`}
                style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>

                {/* ── Status strip ── */}
                <div className={`bg-gradient-to-r ${gradient} px-4 py-2 flex items-center justify-between`}>
                  <span className="text-xs font-bold text-white uppercase tracking-widest">
                    {icon}  {label}
                  </span>
                  {task.scheduled_date && (
                    <span className="text-xs text-white/80">📅 {task.scheduled_date}</span>
                  )}
                </div>

                {/* ── Card body ── */}
                <div className="p-4 cursor-pointer" onClick={() => navigate(`/tasks/${task.id}`)}>

                  <h3 className="font-bold text-slate-800 text-base leading-snug">
                    {task.complaint || t("no_complaint")}
                  </h3>

                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-xs text-slate-700 font-medium">{task.client_name}</span>
                    <span className="text-slate-300">•</span>
                    <span className="text-xs text-slate-500">{task.site_name}</span>
                  </div>

                  {task.scheduled_time && (
                    <p className="text-xs text-slate-400 mt-1">🕐 {task.scheduled_time}</p>
                  )}
                  {task.address && (
                    <p className="text-xs text-slate-400 mt-0.5 truncate">📍 {task.address}</p>
                  )}

                  {task.location_link && (
                    <a href={task.location_link} target="_blank" rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="text-xs text-cyan-400 underline mt-1 block">
                      {t("open_map")}
                    </a>
                  )}

                  {/* Other techs */}
                  {task.assigned_techs?.length > 1 && (
                    <div className="flex gap-1 flex-wrap mt-2">
                      {task.assigned_techs.filter(tech => tech.id !== techId).map((tech, idx) => (
                        <span key={tech.id}
                          className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                          {idx === 0 ? "👑" : "👷"} {tech.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── Actions ── */}
                <div className="px-4 pb-4 space-y-2">

                  {/* Accept / Reject */}
                  {accepted === null && (
                    <div className="flex gap-2">
                      <button onClick={() => handleAccept(task.id)}
                        className="flex-1 py-3 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg shadow-green-900/40 active:scale-95 transition">
                        {t("accept_job")}
                      </button>
                      <button onClick={() => { setRejectModal({ task_id: task.id }); setRejectReason(""); }}
                        className="flex-1 py-3 rounded-xl font-bold text-sm bg-white/10 text-red-400 border border-red-500/30 active:scale-95 transition">
                        {t("reject_job_btn")}
                      </button>
                    </div>
                  )}

                  {/* START WORK — every accepted job can be started (D-306) */}
                  {accepted === true && task.status === SERVICE_STATUS.ASSIGNED && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/tasks/${task.id}`); }}
                      className="w-full py-4 rounded-xl font-bold text-base text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg active:scale-95 transition flex items-center justify-center gap-2">
                      <span className="text-xl">▶</span>
                      {t("start_work")}
                    </button>
                  )}

                  {/* Rejected badge */}
                  {accepted === false && (
                    <div className="py-2.5 px-4 rounded-xl bg-red-50 border border-red-200 text-center">
                      <span className="text-xs text-red-500 font-semibold">{t("you_rejected_job")}</span>
                    </div>
                  )}

                  {/* Continue job */}
                  {isActive && (
                    <button onClick={() => navigate(`/tasks/${task.id}`)}
                      className="w-full py-3.5 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-amber-500 to-orange-500 shadow-lg shadow-amber-900/40 active:scale-95 transition flex items-center justify-center gap-2">
                      {t("continue_job")}
                      <span className="text-xs opacity-80">→</span>
                    </button>
                  )}

                  {/* Pending approval */}
                  {task.status === SERVICE_STATUS.COMPLETED_PENDING_APPROVAL && (
                    <div className="py-3 px-4 rounded-xl bg-violet-50 border border-violet-200 text-center">
                      <p className="text-xs text-violet-600 font-semibold">{t("awaiting_sm_approval")}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── REJECT MODAL ── */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5">
            <div className="mb-4">
              <h3 className="font-bold text-slate-800 text-lg">{t("reject_job_title")}</h3>
              <p className="text-sm text-slate-500 mt-1">{t("reject_reason_note")}</p>
            </div>
            <div className="space-y-3">
              <textarea
                placeholder={t("reject_reason_placeholder")}
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                className="w-full border border-slate-200 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-300 resize-none"
                autoFocus
              />
              <div className="flex gap-3">
                <button onClick={() => { setRejectModal(null); setRejectReason(""); }}
                  className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm">
                  {t("cancel")}
                </button>
                <button onClick={handleReject}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-bold text-sm shadow-lg shadow-red-900/40">
                  {t("confirm_reject")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}